<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">Google Calendar 設定</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" :disabled="saving" @click="handleSaveSettings">
          {{ saving ? '儲存中...' : '儲存設定' }}
        </button>
      </div>
    </div>

    <div class="page-container">

    <!-- 狀態提示 -->
    <div v-if="!loading && !config" class="alert alert-info">
      <i class="ri-information-line"></i>
      <div>
        <h4>尚未連結 Google Calendar</h4>
        <p>請先授權連結 Google 帳號，才能將訂位同步至 Google Calendar。</p>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchConfig">重試</button>
    </div>

    <!-- 未授權狀態 -->
    <div v-else-if="!config" class="card">
      <div class="card-body">
        <h3>連結 Google Calendar</h3>
        <p>將訂位自動同步至您的 Google Calendar，讓您更方便管理預約行程。</p>
        
        <div class="features">
          <div class="feature-item">
            <i class="ri-calendar-check-line"></i>
            <span>自動同步訂位到 Google Calendar</span>
          </div>
          <div class="feature-item">
            <i class="ri-notification-line"></i>
            <span>設定訂位提醒通知</span>
          </div>
          <div class="feature-item">
            <i class="ri-share-line"></i>
            <span>與團隊成員共享行事曆</span>
          </div>
        </div>

        <button 
          class="btn btn-primary btn-lg"
          @click="handleAuthorize"
          :disabled="authorizing"
        >
          <i class="ri-google-line"></i>
          {{ authorizing ? '授權中...' : '使用 Google 帳號授權' }}
        </button>
      </div>
    </div>

    <!-- 已授權狀態 -->
    <div v-else class="settings-container">
      <!-- 連結狀態卡片 -->
      <div class="card status-card">
        <div class="card-body">
          <div class="status-header">
            <div>
              <h3>連結狀態</h3>
              <p class="status-text success">
                <i class="ri-checkbox-circle-line"></i>
                已連結至 Google Calendar
              </p>
            </div>
            <button 
              class="btn btn-danger btn-sm"
              @click="handleRevoke"
            >
              解除連結
            </button>
          </div>
          
          <div class="account-info">
            <div class="info-item">
              <label>Google 帳號</label>
              <span>{{ config.googleAccountEmail }}</span>
            </div>
            <div class="info-item">
              <label>日曆名稱</label>
              <span>{{ config.calendarName || '預設日曆' }}</span>
            </div>
            <div class="info-item">
              <label>最後同步時間</label>
              <span>{{ formatDateTime(config.lastSyncAt) }}</span>
            </div>
            <div class="info-item">
              <label>同步狀態</label>
              <span :class="getSyncStatusClass(config.lastSyncStatus)">
                {{ getSyncStatusText(config.lastSyncStatus) }}
              </span>
            </div>
          </div>

          <div class="calendar-links">
            <h4>日曆連結</h4>
            <button 
              class="btn btn-secondary btn-sm"
              @click="openCalendar"
            >
              <i class="ri-external-link-line"></i>
              在 Google Calendar 中開啟
            </button>
          </div>
        </div>
      </div>

      <!-- 同步設定 -->
      <div class="card">
        <div class="card-body">
          <h3>同步設定</h3>
          
          <form @submit.prevent="handleSaveSettings">
            <div class="form-group">
              <label class="form-label">
                <input 
                  type="checkbox" 
                  v-model="form.syncEnabled"
                  class="form-checkbox"
                />
                啟用自動同步
              </label>
              <small class="form-text">建立或更新訂位時自動同步至 Google Calendar</small>
            </div>

            <div class="form-group">
              <label>同步範圍（天）</label>
              <input 
                type="number" 
                v-model.number="form.syncScope"
                class="form-control"
                min="1"
                max="365"
              />
              <small class="form-text">同步未來幾天內的訂位</small>
            </div>

            <div class="form-group">
              <label>事件標題範本</label>
              <input 
                type="text" 
                v-model="form.eventTitleTemplate"
                class="form-control"
                placeholder="{status}: {name} ({guestCount}人)"
              />
              <small class="form-text">可用變數：{status}, {name}, {guestCount}, {tableNumber}</small>
            </div>

            <div class="form-group">
              <label class="form-label">
                <input 
                  type="checkbox" 
                  v-model="form.includeContactInfo"
                  class="form-checkbox"
                />
                在事件中包含聯絡資訊
              </label>
            </div>

            <div class="form-group">
              <label class="form-label">
                <input 
                  type="checkbox" 
                  v-model="form.enableReminders"
                  class="form-checkbox"
                />
                啟用提醒通知
              </label>
            </div>

            <div v-if="form.enableReminders" class="form-group">
              <label>提前提醒（分鐘）</label>
              <input 
                type="number" 
                v-model.number="form.reminderMinutes"
                class="form-control"
                min="0"
                max="1440"
              />
            </div>
          </form>
        </div>
      </div>

      <!-- 批次同步 -->
      <div class="card">
        <div class="card-body">
          <h3>批次同步</h3>
          <p>手動同步指定日期範圍的訂位至 Google Calendar</p>
          
          <form @submit.prevent="handleBatchSync">
            <div class="form-row">
              <div class="form-group">
                <label>開始日期</label>
                <input 
                  type="date" 
                  v-model="batchSync.dateFrom"
                  class="form-control"
                  required
                />
              </div>
              <div class="form-group">
                <label>結束日期</label>
                <input 
                  type="date" 
                  v-model="batchSync.dateTo"
                  class="form-control"
                  required
                />
              </div>
            </div>

            <button 
              type="submit" 
              class="btn btn-secondary"
              :disabled="syncing"
            >
              <i class="ri-refresh-line"></i>
              {{ syncing ? '同步中...' : '開始批次同步' }}
            </button>
          </form>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, reactive } from 'vue';
import { useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { calendarService } from '@/api/modules/calendar.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'CalendarSettings',
  setup() {
    const route = useRoute();
    const { pageTitle } = usePageTitle('Google Calendar 設定');
    
    const storeId = ref(null);
    const config = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const authorizing = ref(false);
    const saving = ref(false);
    const syncing = ref(false);

    const form = reactive({
      syncEnabled: true,
      syncScope: 30,
      eventTitleTemplate: '{status}: {name} ({guestCount}人)',
      includeContactInfo: true,
      enableReminders: true,
      reminderMinutes: 60
    });

    const batchSync = reactive({
      dateFrom: new Date().toISOString().split('T')[0],
      dateTo: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });

    const fetchConfig = async () => {
      if (!storeId.value) return;
      
      loading.value = true;
      error.value = null;

      try {
        const response = await calendarService.getConfig(storeId.value);
        if (response.success && response.data) {
          config.value = response.data;
          // 填充表單
          Object.assign(form, {
            syncEnabled: response.data.syncEnabled,
            syncScope: response.data.syncScope,
            eventTitleTemplate: response.data.eventTitleTemplate,
            includeContactInfo: response.data.includeContactInfo,
            enableReminders: response.data.enableReminders,
            reminderMinutes: response.data.reminderMinutes
          });
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          error.value = err.response?.data?.message || '載入設定失敗';
        }
      } finally {
        loading.value = false;
      }
    };

    const handleAuthorize = async () => {
      authorizing.value = true;
      try {
        const response = await calendarService.getAuthUrl(storeId.value);
        if (response.success && response.data?.authUrl) {
          // 開啟 OAuth 授權視窗
          window.location.href = response.data.authUrl;
        }
      } catch (err) {
        showError(err.response?.data?.message || '取得授權連結失敗');
      } finally {
        authorizing.value = false;
      }
    };

    const handleSaveSettings = async () => {
      saving.value = true;
      try {
        await calendarService.updateConfig(storeId.value, form);
        showSuccess('設定已儲存');
        fetchConfig();
      } catch (err) {
        showError(err.response?.data?.message || '儲存設定失敗');
      } finally {
        saving.value = false;
      }
    };

    const handleBatchSync = async () => {
      syncing.value = true;
      try {
        const response = await calendarService.batchSync(storeId.value, batchSync);
        if (response.success) {
          const data = response.data;
          showSuccess(`批次同步完成！成功：${data.syncedCount}，失敗：${data.failedCount}`);
          fetchConfig();
        }
      } catch (err) {
        showError(err.response?.data?.message || '批次同步失敗');
      } finally {
        syncing.value = false;
      }
    };

    const handleRevoke = async () => {
      const confirmed = await showDeleteConfirm(
        '確定要解除 Google Calendar 連結嗎？',
        '解除後將無法自動同步訂位至 Google Calendar',
        'warning',
        '確定解除'
      );
      if (!confirmed) return;

      try {
        await calendarService.revokeAuthorization(storeId.value);
        showSuccess('已解除連結');
        config.value = null;
      } catch (err) {
        showError(err.response?.data?.message || '解除連結失敗');
      }
    };

    const openCalendar = async () => {
      try {
        const response = await calendarService.getCalendarLinks(storeId.value);
        if (response.success && response.data?.webLink) {
          window.open(response.data.webLink, '_blank');
        }
      } catch (err) {
        showError('無法開啟 Google Calendar');
      }
    };

    const getSyncStatusText = (status) => {
      const statusMap = {
        'pending': '待同步',
        'synced': '已同步',
        'failed': '同步失敗',
        'auth_required': '需要重新授權'
      };
      return statusMap[status] || status;
    };

    const getSyncStatusClass = (status) => {
      const classMap = {
        'pending': 'badge badge-warning',
        'synced': 'badge badge-success',
        'failed': 'badge badge-danger',
        'auth_required': 'badge badge-warning'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const formatDateTime = (date) => {
      if (!date) return '尚未同步';
      return new Date(date).toLocaleString('zh-TW');
    };

    onMounted(() => {
      // 從路由參數或本地存儲獲取 storeId
      // 這裡簡化處理，實際應該從用戶的門市列表中選擇
      const userStore = localStorage.getItem('currentStoreId');
      storeId.value = route.query.storeId || userStore;
      
      if (storeId.value) {
        fetchConfig();
      }

      // 檢查是否從 OAuth 回調返回
      const code = route.query.code;
      if (code) {
        handleOAuthCallback(code);
      }
    });

    const handleOAuthCallback = async (code) => {
      loading.value = true;
      try {
        await calendarService.authorize(storeId.value, code);
        showSuccess('Google Calendar 連結成功！');
        // 清除 URL 參數
        window.history.replaceState({}, document.title, route.path);
        await fetchConfig();
      } catch (err) {
        showError(err.response?.data?.message || 'OAuth 授權失敗');
      } finally {
        loading.value = false;
      }
    };

    return {
      pageTitle,
      config,
      loading,
      error,
      authorizing,
      saving,
      syncing,
      form,
      batchSync,
      fetchConfig,
      handleAuthorize,
      handleSaveSettings,
      handleBatchSync,
      handleRevoke,
      openCalendar,
      getSyncStatusText,
      getSyncStatusClass,
      formatDateTime
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.alert {
  padding: 20px;
  border-radius: 8px;
  background: #e3f2fd;
  border: 1px solid #2196f3;
  display: flex;
  gap: 15px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.alert i {
  font-size: 24px;
  color: #2196f3;
}

.alert h4 {
  margin: 0 0 8px 0;
  color: #1976d2;
}

.alert p {
  margin: 0;
  color: #424242;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  padding: 0;
  margin-bottom: 20px;
}

.card-body {
  padding: 24px;
}

.card h3 {
  margin: 0 0 16px 0;
  font-size: 20px;
  font-weight: 600;
}

.features {
  margin: 24px 0;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  color: #424242;
}

.feature-item i {
  font-size: 24px;
  color: #4caf50;
}

.btn-lg {
  padding: 12px 24px;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.settings-container {
  display: grid;
  gap: 20px;
}

.status-card .status-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.status-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  margin: 8px 0;
}

.status-text.success {
  color: #4caf50;
}

.status-text i {
  font-size: 20px;
}

.account-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  margin-bottom: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item label {
  font-size: 12px;
  color: #757575;
  font-weight: 500;
}

.info-item span {
  font-size: 14px;
  color: #212121;
}

.calendar-links {
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.calendar-links h4 {
  font-size: 16px;
  margin: 0 0 12px 0;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #212121;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.form-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #757575;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.form-actions {
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

@media (max-width: 768px) {
  .account-info {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
